/**
 * La plantilla de una campaña, en página (`campanas/:id/plantilla?editor=`).
 *
 * El asistente manda acá para crear una plantilla, seguir editando una o partir de un modelo sin perder
 * la campaña (SPEC-BD C1). Es una PÁGINA y no una ventana encima del asistente (pedido de Alan del
 * 23/09: «siempre una pagina, nunca una ventana emergente»): se trabaja a lo ancho, el «atrás» del
 * navegador anda como en cualquier pantalla y un F5 deja todo donde estaba, porque la campaña y la
 * plantilla viven en el servidor y `?editor=` dice qué se estaba haciendo.
 *
 * `?editor=`: `nueva` | `modelo:<clave>` | `plantilla:<id>` | `modelos` (la galería). Sin él, o con uno
 * que no se entiende, es `nueva`: el operador vino a armar una plantilla y eso es lo que ve.
 *
 * El editor no navega: avisa. Esta página asocia a la campaña cada plantilla que el editor crea,
 * duplica o manda, y vuelve al asistente con el aviso o el error en `location.state` (el asistente lo
 * muestra una vez). Las rutas y los textos que comparte con el asistente viven en `logic/editorCampana.ts`.
 * `?publico=` (el público recién creado y sin guardar, R-B12) entra y sale tal cual: esta página no lo
 * usa, pero si lo tirara el asistente volvería con el público viejo elegido.
 */

import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, RefreshCw } from "lucide-react";
import type { CampanaDetalle, PlantillaDetalle } from "../types";
import { oficialApi } from "../api";
import { useCuentaOficial } from "../contexto";
import { useCargaEditorPlantilla } from "../hooks/useCargaEditorPlantilla";
import { AVISO_MANDADA, AVISO_NO_EXISTE, AVISO_YA_ENVIADA, leerEditor, rutaVolverAlAsistente } from "../logic/editorCampana";
import type { VueltaDelEditor } from "../logic/editorCampana";
import { leerError, leerRespuesta, textoError } from "../logic/respuesta";
import { EditorPlantilla } from "../components/editor-plantilla/EditorPlantilla";
import { GaleriaModelos } from "../components/GaleriaModelos";
import { Tarjeta } from "../components/Tarjeta";

const R = "/activa/oficial";
const enc = encodeURIComponent;

/** Lo que se le deja al asistente al volver: `plantillaId` la deja elegida aunque no haya quedado asociada. */
type Vuelta = { plantillaId?: string; aviso?: string; error?: string };

type CargaCampana = { estado: "cargando" } | { estado: "no_existe" } | { estado: "error"; error: string } | { estado: "listo" };

export default function CampanaPlantillaPage() {
  const { id: idCampana = "" } = useParams();
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const { cargando: cargandoCuenta, config, error: errorCuenta, recargar } = useCuentaOficial();

  const pedido = leerEditor(sp.get("editor")) ?? { tipo: "nueva" as const };
  const publico = sp.get("publico") || undefined;

  const [campana, setCampana] = useState<CampanaDetalle | null>(null);
  const [cargaCampana, setCargaCampana] = useState<CargaCampana>({ estado: "cargando" });
  const [intentoCampana, setIntentoCampana] = useState(0);
  const [errorAsociar, setErrorAsociar] = useState("");
  // Lo último que se sabe de la campaña y del error de asociación: los avisos del editor llegan en
  // cadena, y un cierre del render anterior vería la campaña de antes.
  const campanaRef = useRef<CampanaDetalle | null>(null);
  const errorAsociarRef = useRef("");
  // Los avisos del editor pueden llegar con la página ya desmontada: el operador se fue (el «atrás», un
  // link de la sección) con un guardado o un envío en vuelo. El `navigate` de react-router sigue andando
  // después del desmontaje y resolvería contra esta ruta: lo traería de vuelta sin que lo pida. Asociar
  // sí (la plantilla tiene que quedar en la campaña); navegar, no. El `true` del efecto es por el doble
  // montaje de StrictMode.
  const montada = useRef(true);
  useEffect(() => {
    montada.current = true;
    return () => { montada.current = false; };
  }, []);

  /**
   * Vuelve al asistente, al paso de la plantilla. Push sólo desde «Volver a la campaña» (`onCerrar`): la
   * plantilla sigue siendo un borrador y el «atrás» desde el asistente trae de vuelta esta página, que
   * vuelve a pedir todo al servidor. `reemplazar` es para cuando esta entrada ya no puede mostrar nada:
   * la plantilla no existe, ya se mandó a Meta o quedó sin mandar (`unsubmitted`). Si quedara, el «atrás»
   * caería acá y rebotaría otra vez al asistente con un error que el operador no causó, y no podría ir
   * más atrás.
   */
  const volver = ({ plantillaId, aviso, error }: Vuelta = {}, reemplazar = false) => {
    if (!montada.current) return;
    const state: VueltaDelEditor = { ...(aviso ? { avisoPlantilla: aviso } : {}), ...(error ? { errorPlantilla: error } : {}) };
    navigate(rutaVolverAlAsistente(idCampana, plantillaId, publico), { replace: reemplazar, state });
  };
  /** Cambia `?editor=` sin tirar el resto de la URL (`?publico=`). Tampoco navega con la página desmontada. */
  const ponerEditor = (valor: string, reemplazar = false) => {
    if (!montada.current) return;
    setSp((prev) => {
      const n = new URLSearchParams(prev);
      n.set("editor", valor);
      return n;
    }, { replace: reemplazar });
  };
  const volverRef = useRef(volver);
  volverRef.current = volver;

  const { carga, campos, camposFallaron, reintentar, marcarCreada } = useCargaEditorPlantilla({
    id: pedido.tipo === "plantilla" ? pedido.id : undefined,
    modelo: pedido.tipo === "modelo" ? pedido.clave : undefined,
    alNoSerBorrador: () => volverRef.current({ error: AVISO_YA_ENVIADA }, true),
  });

  useEffect(() => {
    let vivo = true;
    setCargaCampana({ estado: "cargando" });
    (async () => {
      try {
        const l = leerRespuesta<CampanaDetalle>(await oficialApi.campana(idCampana));
        if (!vivo) return;
        if (!l.ok) { setCargaCampana(l.status === 404 ? { estado: "no_existe" } : { estado: "error", error: l.error || textoError(l) }); return; }
        if (!l.data) { setCargaCampana({ estado: "error", error: "El servidor no devolvió la campaña." }); return; }
        // Una campaña que ya salió no cambia de plantilla: al detalle, como hace el asistente.
        if (l.data.estado !== "borrador") { navigate(`${R}/campanas/${enc(idCampana)}`, { replace: true }); return; }
        campanaRef.current = l.data;
        setCampana(l.data);
        setCargaCampana({ estado: "listo" });
      } catch (e) {
        if (!vivo) return;
        const x = leerError(e);
        setCargaCampana(x.status === 404 ? { estado: "no_existe" } : { estado: "error", error: textoError(x) });
      }
    })();
    return () => { vivo = false; };
    // Sólo con otro id o al reintentar: `navigate` no cambia nada de lo que se trae.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idCampana, intentoCampana]);

  // Una plantilla que ya no existe no se edita: se vuelve al asistente con el aviso, UNA vez.
  const avisoNoExisteDado = useRef(false);
  useEffect(() => {
    if (carga.estado !== "no_existe" || avisoNoExisteDado.current) return;
    avisoNoExisteDado.current = true;
    volverRef.current({ error: AVISO_NO_EXISTE }, true);
  }, [carga.estado]);

  /**
   * Deja `pid` como la plantilla de la campaña, con la regla del asistente: si reemplaza a otra y la
   * campaña ya tenía variables elegidas, manda `mapeo: {}` (R-B9: el backend conserva el mapeo POR
   * NÚMERO, y el {{1}} = «nombre» de la vieja quedaría aplicado al {{1}} de la nueva, que puede ser
   * otra cosa). Van EN CADENA: el editor avisa «creada» y enseguida «enviada», y el segundo tiene que
   * ver la campaña que dejó el primero; si no, repetiría el PATCH con la campaña vieja.
   * Devuelve si quedó asociada. El error queda arriba del editor y en `errorAsociarRef` para la vuelta.
   */
  const cadenaAsociar = useRef<Promise<boolean>>(Promise.resolve(true));
  const asociar = (pid: string): Promise<boolean> => {
    const fallar = (texto: string) => {
      errorAsociarRef.current = texto;
      setErrorAsociar(texto);
      return false;
    };
    const hacer = async (): Promise<boolean> => {
      const c = campanaRef.current;
      if (!c) return fallar("Todavía no cargó la campaña.");
      const anterior = c.plantilla?.id || "";
      if (anterior === pid) return true;
      const conMapeo = !!anterior && Object.keys(c.mapeo || {}).length > 0;
      try {
        const l = leerRespuesta<CampanaDetalle>(await oficialApi.editarCampana(c.id, { plantillaId: pid, ...(conMapeo ? { mapeo: {} } : {}) }));
        if (!l.ok) return fallar(l.error || textoError(l));
        if (l.data) { campanaRef.current = l.data; setCampana(l.data); }
        errorAsociarRef.current = "";
        setErrorAsociar("");
        return true;
      } catch (e) {
        return fallar(textoError(leerError(e)));
      }
    };
    cadenaAsociar.current = cadenaAsociar.current.then(hacer, hacer);
    return cadenaAsociar.current;
  };

  // Las plantillas que salieron de esta página (creadas o duplicadas acá): ya se están asociando, y el
  // título las da como de la campaña sin esperar a que vuelva el PATCH.
  const propias = useRef(new Set<string>());

  const alCrear = (pid: string) => {
    // Con la marca, pasar a `editor=plantilla:<id>` no la vuelve a traer: lo que está en pantalla ES lo
    // que se guardó, y traerla pisaría lo que el operador siguió escribiendo.
    marcarCreada(pid);
    propias.current.add(pid);
    void asociar(pid);
    // Sin esperar la asociación: si el editor avisa «enviada» enseguida, la página ya se fue al
    // asistente, y un cambio de URL que llegara tarde podría traerla de vuelta. Replace: con
    // `editor=nueva` en el historial, el «atrás» mostraría un editor vacío y parecería que se perdió.
    ponerEditor(`plantilla:${pid}`, true);
  };
  const alEnviar = async (p: PlantillaDetalle) => {
    const asociada = await asociar(p.id);
    // Sin asociar, `?plantilla=` la deja elegida en el asistente: está en revisión en Meta y el operador
    // tiene que verla, no encontrarse con la campaña sin plantilla y sin saber por qué. Replace: ya no
    // es un borrador y esta entrada sólo rebotaría (ver `volver`).
    volver(asociada ? { aviso: AVISO_MANDADA } : {
      plantillaId: p.id, aviso: AVISO_MANDADA, error: `No pudimos dejarla en la campaña: ${errorAsociarRef.current}`,
    }, true);
  };
  /** Twilio creó el Content pero no aceptó el pedido de aprobación: en el asistente queda elegida con «Reintentar el envío». */
  const alNoMandarse = async (pid: string, texto: string) => {
    const asociada = await asociar(pid);
    volver({ ...(asociada ? {} : { plantillaId: pid }), error: texto }, true);
  };
  const alDuplicar = (copia: string) => {
    propias.current.add(copia);
    void asociar(copia);
    // Push: el «atrás» vuelve a la plantilla de la que salió la copia.
    ponerEditor(`plantilla:${copia}`);
  };

  if (cargaCampana.estado === "cargando") return <Esqueleto etiqueta="Cargando la campaña" />;
  if (cargaCampana.estado === "no_existe") {
    return (
      <Tarjeta>
        <p className="text-sm font-semibold text-slate-800">No encontramos esta campaña.</p>
        <Link to={`${R}/campanas`} className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-violet-700 hover:underline">
          <ArrowLeft size={14} /> Volver a las campañas
        </Link>
      </Tarjeta>
    );
  }
  if (cargaCampana.estado === "error" || !campana) {
    return (
      <Tarjeta>
        <p className="text-sm font-semibold text-red-700">No pudimos traer la campaña.</p>
        <p className="text-sm text-slate-600 mt-1">{cargaCampana.estado === "error" ? cargaCampana.error : "El servidor no devolvió la campaña."}</p>
        <button type="button" onClick={() => setIntentoCampana((n) => n + 1)} className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-violet-700 hover:underline">
          <RefreshCw size={14} /> Reintentar
        </button>
      </Tarjeta>
    );
  }

  // «Seguir editando» ofrece todos los borradores de la marca y abrir uno no lo asocia (queda en la
  // campaña al mandarlo): el título no puede decir que es de la campaña si no lo es.
  const deLaCampana = pedido.tipo === "plantilla" && (campana.plantilla?.id === pedido.id || propias.current.has(pedido.id));
  const titulo = pedido.tipo === "modelos" ? `Modelos Deenex para «${campana.nombre}»`
    : deLaCampana ? `Plantilla de «${campana.nombre}»`
    : pedido.tipo === "plantilla" ? `Un borrador para «${campana.nombre}»`
    : `Nueva plantilla para «${campana.nombre}»`;
  const cabecera = (
    <div>
      <h2 className="text-lg font-semibold text-slate-900 tracking-tight">{titulo}</h2>
      {pedido.tipo === "modelos" && <p className="text-xs text-slate-500 mt-0.5">Ya pasan las reglas de Meta. Elegí uno y ajustalo a tu marca.</p>}
      {pedido.tipo === "plantilla" && !deLaCampana && (
        <p className="text-xs text-slate-500 mt-0.5">Todavía no es la plantilla de esta campaña: queda en ella cuando la mandes a Meta.</p>
      )}
    </div>
  );

  if (pedido.tipo !== "modelos" && config && carga.estado === "listo") {
    // Con el editor, el «volver» lo pone él (arriba de la cabecera, como en cualquier página): tiene que
    // preguntar antes de tirar cambios sin guardar, y dos «Volver a la campaña» dejarían uno que no pregunta.
    return (
      <EditorPlantilla
        key={carga.clave}
        modo="campana"
        encabezado={<>
          {cabecera}
          {errorAsociar && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">{errorAsociar}</p>}
        </>}
        inicial={carga.inicial}
        idInicial={carga.id}
        avisoInicial={carga.aviso}
        config={config}
        campos={campos}
        camposFallaron={camposFallaron}
        onCreada={alCrear}
        onEnviada={(p) => void alEnviar(p)}
        onUnsubmitted={(pid, texto) => void alNoMandarse(pid, texto)}
        onDuplicada={alDuplicar}
        onCerrar={() => volver()}
      />
    );
  }

  let contenido: ReactNode;
  if (pedido.tipo === "modelos") {
    // Push: el «atrás» desde el modelo elegido vuelve a la galería.
    contenido = <GaleriaModelos onElegir={(clave) => ponerEditor(`modelo:${clave}`)} />;
  } else if (!config) {
    contenido = cargandoCuenta ? <Esqueleto etiqueta="Cargando la plantilla" /> : (
      <Tarjeta>
        <p className="text-sm font-semibold text-red-700">No pudimos traer la configuración de la marca.</p>
        <p className="text-sm text-slate-600 mt-1">{errorCuenta || "Sin la configuración no sabemos el texto de baja ni el idioma: no se puede armar la plantilla."}</p>
        <button type="button" onClick={() => void recargar()} className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-violet-700 hover:underline">
          <RefreshCw size={14} /> Reintentar
        </button>
      </Tarjeta>
    );
  } else if (carga.estado === "error") {
    contenido = (
      <Tarjeta>
        <p className="text-sm font-semibold text-red-700">No pudimos traer la plantilla.</p>
        <p className="text-sm text-slate-600 mt-1">{carga.error}</p>
        <button type="button" onClick={reintentar} className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-violet-700 hover:underline">
          <RefreshCw size={14} /> Reintentar
        </button>
      </Tarjeta>
    );
  } else {
    // Cargando, o «no_existe», que ya se está yendo al asistente con el aviso.
    contenido = <Esqueleto etiqueta="Cargando la plantilla" />;
  }

  // Fuera del editor no hay cambios que perder: el «volver» es un link de la página.
  return (
    <div className="space-y-5">
      <Link to={rutaVolverAlAsistente(idCampana, undefined, publico)} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800">
        <ArrowLeft size={14} /> Volver a la campaña
      </Link>
      {cabecera}
      {contenido}
    </div>
  );
}

function Esqueleto({ etiqueta }: { etiqueta: string }) {
  return (
    <div className="space-y-4" aria-busy="true" aria-label={etiqueta}>
      <div className="h-24 rounded-2xl bg-slate-100 animate-pulse" />
      <div className="grid lg:grid-cols-[1fr_300px] gap-4">
        <div className="h-96 rounded-2xl bg-slate-100 animate-pulse" />
        <div className="h-96 rounded-2xl bg-slate-100 animate-pulse" />
      </div>
    </div>
  );
}
